// Location services. Web: navigator.geolocation (foreground only). Capacitor: swap for @capacitor/geolocation.
export type GeoPoint = { lat: number; lng: number; accuracy?: number };
export type PermissionState = "granted" | "denied" | "prompt" | "unsupported";

const supported = () => typeof navigator !== "undefined" && "geolocation" in navigator;

const toError = (e: GeolocationPositionError) =>
  new Error(e.code === 1 ? "Location permission denied. Enable it in your browser settings." :
    e.code === 3 ? "Location timed out. Try again or pick on the map." : "Location unavailable.");

export const location = {
  async checkPermission(): Promise<PermissionState> {
    if (!supported()) return "unsupported";
    try {
      const res = await (navigator as any).permissions?.query({ name: "geolocation" });
      return (res?.state as PermissionState) || "prompt";
    } catch {
      return "prompt";
    }
  },
  getCurrentPosition(): Promise<GeoPoint> {
    return new Promise((resolve, reject) => {
      if (!supported()) return reject(new Error("Location is not supported on this device."));
      navigator.geolocation.getCurrentPosition(
        (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
        (e) => reject(toError(e)),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
      );
    });
  },
  watchPosition(onPoint: (p: GeoPoint) => void, onError: (e: Error) => void): () => void {
    if (!supported()) { onError(new Error("Location is not supported on this device.")); return () => {}; }
    const id = navigator.geolocation.watchPosition(
      (p) => onPoint({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      (e) => onError(toError(e)),
      { enableHighAccuracy: true, maximumAge: 10000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  },
  backgroundTrackingAvailable: false,
};
